import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { ArrowRight, Disc3, Star, Users, LayoutList, Heart } from "lucide-react";
import { useState, useEffect } from "react";

// Vinyl Disc Icon Component (keep for features section)
const VinylIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 100 100" className={className}>
    <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" strokeWidth="3"/>
    <circle cx="50" cy="50" r="36" fill="none" stroke="currentColor" strokeWidth="2"/>
    <circle cx="50" cy="50" r="24" fill="none" stroke="currentColor" strokeWidth="2"/>
    <circle cx="50" cy="50" r="12" fill="none" stroke="currentColor" strokeWidth="2"/>
    <circle cx="50" cy="50" r="4" fill="currentColor"/>
  </svg>
);

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [featuredAlbums, setFeaturedAlbums] = useState([
    { type: "new", cover: "https://e-cdns-images.dzcdn.net/images/cover/d2a567793da559dccaae10bf2c0ed1f1/500x500-000000-80-0-0.jpg", title: "New Release", artist: "" },
    { type: "classic", cover: "https://e-cdns-images.dzcdn.net/images/cover/ec3c8ed67427064c70f67e5571f05566/500x500-000000-80-0-0.jpg", title: "Classic", artist: "" },
    { type: "wildcard", cover: "https://e-cdns-images.dzcdn.net/images/cover/1594e427eb97a520845617e06c7de782/500x500-000000-80-0-0.jpg", title: "Discover", artist: "" },
  ]);

  // Fetch weekly featured albums (non-blocking)
  useEffect(() => {
    const fetchFeaturedAlbums = async () => {
      try {
        // Use week number as seed for consistent weekly rotation
        const weekNumber = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
        
        // Fetch classic rock / indie for hero image
        const rockIndieGenres = [152, 85]; // Rock, Alternative/Indie
        const rockGenreId = rockIndieGenres[weekNumber % rockIndieGenres.length];
        const rockRes = await fetch(`${API}/deezer/genre/${rockGenreId}/albums?limit=10`);
        const rockAlbums = rockRes.ok ? await rockRes.json() : [];
        
        // Fetch hip hop / pop for second image
        const hipHopPopGenres = [116, 132]; // Hip Hop, Pop
        const hipHopGenreId = hipHopPopGenres[weekNumber % hipHopPopGenres.length];
        const hipHopRes = await fetch(`${API}/deezer/genre/${hipHopGenreId}/albums?limit=10`);
        const hipHopAlbums = hipHopRes.ok ? await hipHopRes.json() : [];
        
        // Fetch wildcard from various genres
        const wildcardGenres = [129, 106, 165, 466, 464, 98, 144]; // Jazz, Electronic, R&B, Folk, Metal, Classical, Reggae
        const wildcardGenreId = wildcardGenres[weekNumber % wildcardGenres.length];
        const wildcardRes = await fetch(`${API}/deezer/genre/${wildcardGenreId}/albums?limit=10`);
        const wildcardAlbums = wildcardRes.ok ? await wildcardRes.json() : [];
        
        // Pick albums based on week number for variety
        const rockIndex = weekNumber % Math.max(rockAlbums.length, 1);
        const hipHopIndex = (weekNumber + 3) % Math.max(hipHopAlbums.length, 1);
        const wildcardIndex = (weekNumber + 5) % Math.max(wildcardAlbums.length, 1);
        
        if (rockAlbums.length > 0 || hipHopAlbums.length > 0 || wildcardAlbums.length > 0) {
          setFeaturedAlbums([
            { 
              type: "rock", 
              cover: rockAlbums[rockIndex]?.cover_xl || rockAlbums[rockIndex]?.cover_big || featuredAlbums[0].cover,
              title: rockAlbums[rockIndex]?.title || "Classic Rock",
              artist: rockAlbums[rockIndex]?.artist || ""
            },
            { 
              type: "hiphop", 
              cover: hipHopAlbums[hipHopIndex]?.cover_xl || hipHopAlbums[hipHopIndex]?.cover_big || featuredAlbums[1].cover,
              title: hipHopAlbums[hipHopIndex]?.title || "Hip Hop",
              artist: hipHopAlbums[hipHopIndex]?.artist || ""
            },
            { 
              type: "wildcard", 
              cover: wildcardAlbums[wildcardIndex]?.cover_xl || wildcardAlbums[wildcardIndex]?.cover_big || featuredAlbums[2].cover,
              title: wildcardAlbums[wildcardIndex]?.title || "Discover",
              artist: wildcardAlbums[wildcardIndex]?.artist || ""
            },
          ]);
        }
      } catch (error) {
        console.error("Failed to fetch featured albums:", error);
        // Keep default albums on error
      }
    };
    
    fetchFeaturedAlbums();
  }, []);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    {
      icon: VinylIcon,
      title: "Track Your Listens",
      description: "Log every album you listen to. Build your personal music diary.",
    },
    {
      icon: Star,
      title: "Rate & Review",
      description: "Share your thoughts with ratings and in-depth reviews.",
    },
    {
      icon: LayoutList,
      title: "Create Lists",
      description: "Curate your favorites into custom collections and playlists.",
    },
    {
      icon: Users,
      title: "Connect",
      description: "Follow friends and discover music through their taste.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#5B21B6]" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#6D28D9] via-[#5B21B6] to-[#4C1D95]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/5 via-transparent to-transparent" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            {/* Left side - Text content */}
            <div className="max-w-2xl text-center lg:text-left">
              {/* Official Logo - All White for purple background */}
              <div className="mb-8 animate-fade-in flex justify-center lg:justify-start">
                <img 
                  src="/images/logo-white.svg" 
                  alt="Sonary" 
                  className="h-20 sm:h-24 md:h-28 w-auto"
                />
              </div>

              {/* Headline */}
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6 animate-slide-up text-white"
                style={{ animationDelay: "0.1s" }}
              >
                Your sonic library,
                <br />
                <span className="text-white/90">reimagined</span>
              </h1>

              {/* Subheadline */}
              <p 
                className="text-lg md:text-xl text-white/70 max-w-xl mb-10 animate-slide-up mx-auto lg:mx-0"
                style={{ animationDelay: "0.2s" }}
              >
                Track albums you love, rate your favorites, and connect with fellow 
                music enthusiasts. Your personal music journey starts here.
              </p>

              {/* CTA */}
              <div 
                className="flex flex-col sm:flex-row gap-4 animate-slide-up justify-center lg:justify-start"
                style={{ animationDelay: "0.3s" }}
              >
                <Button
                  onClick={handleLogin}
                  className="rounded-full bg-white text-[#5B21B6] hover:bg-white/90 hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] transition-all duration-300 font-bold px-8 py-6 text-lg"
                  data-testid="get-started-btn"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full border-white/30 hover:bg-white/10 px-8 py-6 text-lg text-white"
                  onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                >
                  Learn More
                </Button>
              </div>

              {/* Alternative login */}
              <p 
                className="text-sm text-white/60 mt-4 animate-slide-up"
                style={{ animationDelay: "0.35s" }}
              >
                Or{" "}
                <Link to="/signup" className="text-white/90 hover:text-white underline underline-offset-2">
                  sign up with email
                </Link>
                {" · "}
                <Link to="/login" className="text-white/90 hover:text-white underline underline-offset-2">
                  sign in
                </Link>
              </p>

              {/* Stats */}
              <div 
                className="flex gap-8 mt-16 animate-slide-up justify-center lg:justify-start"
                style={{ animationDelay: "0.4s" }}
              >
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">10K+</p>
                  <p className="text-sm text-white/60">Albums Logged</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-white/90">5K+</p>
                  <p className="text-sm text-white/60">Reviews Written</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-white/80">2K+</p>
                  <p className="text-sm text-white/60">Active Users</p>
                </div>
              </div>
            </div>

            {/* Right side - Weekly Featured Albums (Overlapping) */}
            <div className="hidden lg:block relative w-96 h-80">
              {featuredAlbums.map((album, index) => {
                const positions = [
                  { top: "0", right: "0", size: "w-52 h-52", zIndex: "z-30", rotation: "rotate-6" },
                  { top: "80px", right: "180px", size: "w-44 h-44", zIndex: "z-20", rotation: "-rotate-6" },
                  { top: "20px", right: "320px", size: "w-36 h-36", zIndex: "z-10", rotation: "rotate-12" }
                ];
                const pos = positions[index];
                
                return (
                  <div 
                    key={album.type} 
                    className={`absolute ${pos.zIndex}`}
                    style={{ 
                      top: pos.top, 
                      right: pos.right,
                      animationDelay: `${0.2 + index * 0.1}s` 
                    }}
                  >
                    <div className={`${pos.size} rounded-xl overflow-hidden shadow-2xl transform ${pos.rotation} transition-transform duration-300 hover:scale-105`}>
                      <img 
                        src={album.cover || `https://placehold.co/400x400/1a1a2e/9333ea?text=${album.type}`}
                        alt={`${album.title} by ${album.artist}`}
                        className="w-full h-full object-cover"
                        onError={(e) => { 
                          e.target.src = `https://placehold.co/400x400/1a1a2e/9333ea?text=${album.type}`;
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4 sm:px-6 lg:px-8 bg-[#4C1D95]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4 text-white">
              Everything you need
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              A complete platform for tracking, rating, and sharing your music journey.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-white/30 hover:bg-white/10 transition-all duration-300"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-white">{feature.title}</h3>
                  <p className="text-sm text-white/60">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#5B21B6]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6 text-white">
            Ready to start your journey?
          </h2>
          <p className="text-lg text-white/60 mb-10">
            Join thousands of music lovers who track their listening habits with Sonary.
          </p>
          <Button
            onClick={handleLogin}
            className="rounded-full bg-white text-[#5B21B6] hover:bg-white/90 hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] transition-all duration-300 font-bold px-10 py-6 text-lg"
            data-testid="cta-get-started-btn"
          >
            Sign Up with Google
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/10 bg-[#4C1D95]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <img 
            src="/images/logo-white.svg" 
            alt="Sonary" 
            className="h-8 w-auto"
          />
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-sm text-white/50 hover:text-white/80 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-sm text-white/50 hover:text-white/80 transition-colors">
              Terms of Service
            </Link>
            <p className="text-sm text-white/50">
              © 2025 Sonary. Made with love for music.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export { VinylIcon };
